import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { CheckCircle2, Download, Eye, Loader2, PenLine, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { LOGO_URL, PHONES, PHONE_LINKS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function QuoteView() {
  const { id } = useParams();
  const { search } = useLocation();
  const isPreview = new URLSearchParams(search).get("preview") === "1";
  const [quote, setQuote] = useState(undefined);

  useEffect(() => {
    fetch(`${API}/quotes/${id}${isPreview ? "?preview=1" : ""}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setQuote)
      .catch(() => setQuote(null));
  }, [id, isPreview]);

  if (quote === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><Loader2 className="w-6 h-6 text-amber-700 animate-spin" /></div>;
  }
  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] px-4 text-center">
        <div>
          <h1 className="font-display text-2xl text-stone-900 mb-2">Quote not found</h1>
          <p className="text-stone-500 text-sm">This link may be incorrect — call us at {PHONES[0]} and we'll help.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-10 px-4" data-testid="quote-page">
      <Seo title={`Quote ${quote.number} | Crownvera Epoxy`} description="Your Crownvera Epoxy quote." noindex />
      <div className="max-w-3xl mx-auto">
        <div className="gold-ring-card rounded-xl overflow-hidden">
          <div className="bg-stone-950 px-6 sm:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
            <img src={LOGO_URL} alt="Crownvera Epoxy" className="h-14 w-auto" />
            <div className="text-right">
              <p className="font-display text-2xl text-white tracking-wide">QUOTE</p>
              <p className="text-amber-400 text-sm font-semibold" data-testid="quote-number">{quote.number}</p>
              <p className="text-stone-400 text-xs mt-1">{new Date(quote.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            </div>
          </div>

          <div className="p-6 sm:p-10">
            {isPreview && (
              <div className="mb-6 flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="quote-preview-banner">
                <Eye className="w-5 h-5 text-amber-700 shrink-0" />
                <p className="text-sm text-amber-900">Preview mode — this is exactly what your customer sees. Viewing this does not mark the quote as viewed.</p>
              </div>
            )}
            {quote.signed && (
              <div className="mb-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="quote-signed-banner">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <p className="text-sm text-emerald-800">
                  Signed &amp; accepted by {quote.signed.name} on {new Date(quote.signed.date).toLocaleDateString()}.
                </p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700 mb-2">Prepared For</p>
                <p className="font-semibold text-stone-900">{quote.customer?.name}</p>
                {quote.customer?.business && <p className="text-sm text-stone-600">{quote.customer.business}</p>}
                {quote.customer?.project_address && <p className="text-sm text-stone-600">{quote.customer.project_address}</p>}
                {quote.customer?.phone && <p className="text-sm text-stone-600">{quote.customer.phone}</p>}
                {quote.customer?.email && <p className="text-sm text-stone-600">{quote.customer.email}</p>}
                <p className="text-xs text-stone-500 mt-2 capitalize">{quote.project_type} project</p>
              </div>
              <div className="sm:text-right">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700 mb-2">Crownvera Epoxy LLC</p>
                <p className="text-sm text-stone-600">Roman Pashkovskiy, Owner</p>
                <p className="text-sm text-stone-600">Cleveland, Ohio</p>
                <div className="sm:justify-end flex gap-4 mt-2">
                  {PHONES.map((p, i) => (
                    <a key={p} href={PHONE_LINKS[i]} className="text-sm text-amber-800 font-semibold flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" />{p}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <table className="w-full text-sm" data-testid="quote-items">
              <thead>
                <tr className="border-b-2 border-amber-600/30 text-left">
                  <th className="py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold">Description</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Qty</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Unit</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {quote.line_items.map((it, i) => (
                  <tr key={i} className="border-b border-amber-600/10">
                    <td className="py-3 text-stone-800">{it.description}</td>
                    <td className="py-3 text-right text-stone-600">{it.qty}</td>
                    <td className="py-3 text-right text-stone-600">${Number(it.unit_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                    <td className="py-3 text-right font-semibold text-stone-900">${(it.qty * it.unit_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end mt-4">
              <p className="font-display text-3xl text-stone-900" data-testid="quote-total-display">
                Total: <span className="text-amber-800">${Number(quote.total).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </p>
            </div>

            {quote.notes && (
              <div className="mt-6 bg-[#F5F2EB] rounded-lg p-5">
                <p className="text-xs uppercase tracking-wider font-semibold text-amber-700 mb-1">Notes</p>
                <p className="text-sm text-stone-700 whitespace-pre-line">{quote.notes}</p>
              </div>
            )}

            <div className="mt-8 text-xs text-stone-500 leading-relaxed border-t border-amber-600/15 pt-5">
              <p><span className="font-semibold text-stone-700">Payment:</span> Projects $25,000 or less — 50% down before work begins, remaining 50% due immediately upon completion. Larger residential projects follow the progress-payment schedule in the Project Agreement.</p>
              <p className="mt-2"><span className="font-semibold text-stone-700">Warranty:</span> Every finish is backed by the Crownvera 10-Year Limited Coating Warranty.</p>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              <a
                href={`${BACKEND}/api/quotes/${quote.id}/pdf`}
                data-testid="quote-download-pdf"
                className="border border-amber-600/40 hover:border-amber-600 text-amber-800 font-semibold uppercase tracking-wider text-sm py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
              {!quote.signed && !isPreview && (
                <Link
                  to={`/quote/${quote.id}/agreement`}
                  data-testid="quote-sign-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm py-4 rounded-md shadow-md transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  <PenLine className="w-4 h-4" /> Review & Sign Agreement
                </Link>
              )}
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-stone-400 mt-6">Crownvera Epoxy LLC · Cleveland, OH · crownveraepoxy.com</p>
      </div>
    </div>
  );
}
